import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Grid,
  GridItem,
  Icon,
  Button,
  Flex,
  Link,
  Divider,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import {
  FaCheck,
  FaPhone,
  FaChevronRight,
  FaLock,
  FaRegFileAlt,
} from 'react-icons/fa';
import OptimizedImage from '../components/common/OptimizedImage';
import { CATALYST_URL, isEstimatorLive } from '../config/priceTransparency';
import { PageHero, SectionHeading, IconMark, InkBand, LightSheet } from '../components/premium';
import { Reveal, RevealGroup, RevealItem, StackCards } from '../components/motion';
import { clearGlass } from '../components/glass';

// One quiet card surface: white, hairline edge, soft ink shadow.
const card = {
  bg: 'white',
  borderRadius: '24px',
  boxShadow: 'card',
  border: '1px solid',
  borderColor: 'rgba(14,47,58,0.06)',
};

// Cards that are not sticky also settle upward slightly on hover.
const hoverSettle = {
  transition: 'transform 0.5s cubic-bezier(0.16,1,0.3,1)',
  _hover: { transform: 'translateY(-3px)' },
};

const PriceTransparency = () => {
  // How Catalyst builds a patient-specific number. Written as what the tool
  // does for the patient, not as a description of the software.
  const steps = [
    {
      title: 'We verify your coverage',
      description:
        'Your plan details are confirmed directly with your insurer in real time — no guesswork and no waiting on a callback.',
    },
    {
      title: 'We read your current benefits',
      description:
        'Your deductible, co-insurance, and out-of-pocket maximum as they stand today, not as they stood in January.',
    },
    {
      title: 'We calculate your share',
      description:
        'Your procedure, our negotiated rate with your insurer, and your benefits at that moment resolve into a single number.',
    },
  ];

  const included = [
    'Facility fee for your procedure at Lake Lucien Surgery Center',
    'Operating room, recovery, and nursing care during your visit',
    'Standard supplies and medications administered here',
  ];

  const separate = [
    'Your surgeon’s professional fee',
    'Anesthesia services',
    'Pathology, laboratory, and radiology reads',
    'Implants or devices billed by the manufacturer',
  ];

  return (
    <Box>
      {/* ── Hero ─────────────────────────────────────────────────── */}
      {/* Photographic hero, shared with every inner page. See
          components/premium/PageHero for why the photo is no longer
          hidden under a cyan gradient. */}
      <PageHero
        title={["Know what your surgery costs", "before you schedule it"]}
        subtitle={"A clear, personalized estimate built from your plan and your benefits \u2014 not a published average, and not a surprise that arrives weeks later."}
        image="/facility/exterior.jpg"
        imagePosition="center 30%"
      />

      {/* ── Estimate CTA — overlaps the hero so it is the first thing
             below the fold on every screen size ───────────────────── */}
      <Container maxW="container.xl" mt={{ base: -12, md: -20 }} position="relative" zIndex={2}>
        <Box
          bg="white"
          borderRadius="24px"
          boxShadow="card"
          border="1px solid"
          borderColor="rgba(14,47,58,0.06)"
          p={{ base: 8, md: 14 }}
          maxW="1320px"
          mx="auto"
        >
          <Grid
            templateColumns={{ base: '1fr', lg: '1fr 380px' }}
            gap={{ base: 8, lg: 16 }}
            alignItems="center"
          >
            <GridItem>
              <Heading size="xl" color="ink.900" fontWeight="600" mb={4} letterSpacing="-0.03em">
                Request your estimate
              </Heading>
              <Text color="ink.600" fontSize="lg" lineHeight="1.7" maxW="2xl">
                {isEstimatorLive
                  ? 'Answer a few questions about your procedure and your insurance. You will see what you can expect to pay at Lake Lucien Surgery Center before anything is scheduled.'
                  : 'Our online estimator launches with the surgery center. Until then, our business office will walk you through pricing for your procedure and answer any question about what you will owe.'}
              </Text>
            </GridItem>

            <GridItem>
              <VStack spacing={3} align="stretch">
                {isEstimatorLive ? (
                  <Button
                    as="a"
                    href={CATALYST_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="lg"
                    py={8}
                    bg="accent.400"
                    color="ink.900"
                    fontSize="md"
                    fontWeight="700"
                    _hover={{ bg: 'accent.300', transform: 'translateY(-2px)' }}
                    transition="all 0.25s"
                    rightIcon={<FaChevronRight />}
                  >
                    Get an online estimate
                  </Button>
                ) : (
                  <Button
                    as="a"
                    href="tel:+13213213935"
                    size="lg"
                    py={8}
                    bg="accent.400"
                    color="ink.900"
                    fontSize="md"
                    fontWeight="700"
                    _hover={{ bg: 'accent.300', transform: 'translateY(-2px)' }}
                    transition="all 0.25s"
                    leftIcon={<Icon as={FaPhone} />}
                  >
                    (321) 321-3935
                  </Button>
                )}

                <Button
                  as={RouterLink}
                  to="/contact"
                  size="lg"
                  py={7}
                  variant="outline"
                  borderColor="blackAlpha.300"
                  color="ink.700"
                  fontWeight="700"
                  fontSize="md"
                  _hover={{ bg: 'gray.50', borderColor: 'ink.400' }}
                  transition="all 0.25s"
                >
                  Message our business office
                </Button>

                <HStack spacing={2} justify="center" pt={2}>
                  <Icon as={FaLock} boxSize={3} color="ink.400" />
                  <Text fontSize="xs" color="ink.500">
                    Handled under HIPAA safeguards
                  </Text>
                </HStack>
              </VStack>
            </GridItem>
          </Grid>
        </Box>
      </Container>

      {/* ── How it works — intro holds on the left while the three steps
             stack on the right. The steps are an ordered sequence, so each
             one stays visible as a ledge once the reader has passed it. ── */}
      <Box py={{ base: 16, md: 28 }}>
        <Container maxW="container.xl">
          <Grid
            maxW="1320px"
            mx="auto"
            templateColumns={{ base: '1fr', lg: '5fr 7fr' }}
            gap={{ base: 10, lg: 20 }}
            alignItems="start"
          >
            <GridItem position={{ base: 'static', lg: 'sticky' }} top={{ lg: '120px' }}>
              <SectionHeading
                align="left"
                mb={0}
                eyebrow="How the estimate is built"
                title="A real number, not a price range"
                lede="Most published hospital pricing is a chargemaster spreadsheet that tells you almost nothing about your own bill. Ours starts from your plan."
              />
            </GridItem>

            <GridItem>
              <StackCards>
                {steps.map((step, index) => (
                  <Box key={step.title} {...card} p={{ base: 8, md: 10 }}>
                    <Text
                      fontSize={{ base: '4xl', md: '5xl' }}
                      fontWeight="300"
                      color="brand.600"
                      lineHeight="1"
                      letterSpacing="-0.04em"
                      fontFamily="heading"
                      mb={{ base: 6, md: 8 }}
                    >
                      {String(index + 1).padStart(2, '0')}
                    </Text>
                    <Heading size="md" color="ink.900" fontWeight="600" mb={2} letterSpacing="-0.02em">
                      {step.title}
                    </Heading>
                    <Text color="ink.600" lineHeight="1.7">
                      {step.description}
                    </Text>
                  </Box>
                ))}
              </StackCards>
            </GridItem>
          </Grid>
        </Container>
      </Box>

      {/* ── What the estimate covers ─────────────────────────────── */}
      <LightSheet tone="mist" py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <Box maxW="1320px" mx="auto">
            <SectionHeading
              align="left"
              eyebrow="What is in the number"
              title="Clear about what we can and cannot quote"
            />

            <RevealGroup
              display="grid"
              gridTemplateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }}
              gap={{ base: 6, lg: 10 }}
            >
              <RevealItem>
                <Box {...card} {...hoverSettle} p={{ base: 8, md: 10 }} h="100%">
                  <Heading size="md" color="ink.900" fontWeight="600" mb={7} letterSpacing="-0.02em">
                    Your estimate includes
                  </Heading>
                  <VStack align="stretch" spacing={0}>
                    {included.map((item, i) => (
                      <Box key={item}>
                        {i > 0 && <Divider borderColor="blackAlpha.100" />}
                        <HStack align="start" spacing={4} py={4}>
                          <Flex
                            w="22px"
                            h="22px"
                            borderRadius="full"
                            bg="brand.50"
                            border="1px solid"
                            borderColor="rgba(14,47,58,0.08)"
                            align="center"
                            justify="center"
                            flexShrink={0}
                            mt="2px"
                          >
                            <Icon as={FaCheck} color="brand.600" boxSize={2.5} />
                          </Flex>
                          <Text color="ink.700" lineHeight="1.65">
                            {item}
                          </Text>
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                </Box>
              </RevealItem>

              <RevealItem>
                <Box {...card} {...hoverSettle} p={{ base: 8, md: 10 }} h="100%">
                  <Heading size="md" color="ink.900" fontWeight="600" mb={7} letterSpacing="-0.02em">
                    Billed separately
                  </Heading>
                  <VStack align="stretch" spacing={0}>
                    {separate.map((item, i) => (
                      <Box key={item}>
                        {i > 0 && <Divider borderColor="blackAlpha.100" />}
                        <HStack align="start" spacing={4} py={4}>
                          <Box
                            w="22px"
                            h="22px"
                            flexShrink={0}
                            mt="2px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Box w="10px" h="2px" bg="ink.400" borderRadius="full" />
                          </Box>
                          <Text color="ink.700" lineHeight="1.65">
                            {item}
                          </Text>
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                  <Text color="ink.500" fontSize="sm" mt={7} lineHeight="1.65">
                    These providers bill independently of the surgery center. Ask us and we will
                    point you to the right office for each one.
                  </Text>
                </Box>
              </RevealItem>
            </RevealGroup>
          </Box>
        </Container>
      </LightSheet>

      {/* ── Good Faith Estimate ──────────────────────────────────── */}
      <Container maxW="container.xl" py={{ base: 16, md: 24 }}>
        <Reveal maxW="1320px" mx="auto">
          <Grid
            templateColumns={{ base: '1fr', lg: '360px 1fr' }}
            gap={{ base: 8, lg: 16 }}
            alignItems="start"
          >
            <GridItem>
              <IconMark icon={FaRegFileAlt} mb={5} />
              <Heading size="xl" color="ink.900" fontWeight="600" letterSpacing="-0.03em" lineHeight="1.15">
                If you are uninsured or paying out of pocket
              </Heading>
            </GridItem>

            <GridItem>
              <VStack align="stretch" spacing={5}>
                <Text color="ink.600" fontSize="lg" lineHeight="1.75">
                  You have the right to a{' '}
                  <Box as="strong" color="ink.900" fontWeight="700">
                    Good Faith Estimate
                  </Box>{' '}
                  explaining how much your care will cost. Ask us for one at any point, and we will
                  provide it in writing before your procedure.
                </Text>
                <Text color="ink.600" fontSize="lg" lineHeight="1.75">
                  If you are billed substantially more than your Good Faith Estimate, you may be
                  able to dispute the bill. Keep a copy of your estimate — our business office can
                  send another at any time.
                </Text>
                <HStack spacing={3} pt={2}>
                  <Link
                    as={RouterLink}
                    to="/contact"
                    color="brand.600"
                    fontWeight="700"
                    display="inline-flex"
                    alignItems="center"
                    gap={2}
                    _hover={{ color: 'brand.700', textDecoration: 'none', gap: 3 }}
                    transition="all 0.2s"
                  >
                    Request a Good Faith Estimate
                    <Icon as={FaChevronRight} boxSize={3} />
                  </Link>
                </HStack>
              </VStack>
            </GridItem>
          </Grid>
        </Reveal>
      </Container>

      {/* ── Powered by HST Pathways ──────────────────────────────── */}
      <Box borderY="1px solid" borderColor="rgba(14,47,58,0.06)" py={{ base: 12, md: 16 }}>
        <Container maxW="container.xl">
          <Reveal maxW="1320px" mx="auto">
            <Grid
              templateColumns={{ base: '1fr', md: '220px 1fr' }}
              gap={{ base: 8, md: 14 }}
              alignItems="center"
            >
              <GridItem>
                <Text
                  fontSize="xs"
                  fontWeight="600"
                  textTransform="uppercase"
                  letterSpacing="0.18em"
                  color="ink.500"
                  mb={4}
                >
                  Powered by
                </Text>
                <Link
                  href="https://www.hstpathways.com"
                  isExternal
                  display="block"
                  _hover={{ opacity: 0.75 }}
                  transition="opacity 0.2s"
                >
                  {/* Explicit height matters: an unloaded lazy image with only
                      a width collapses to 0px tall, so it never intersects the
                      viewport and never loads itself. */}
                  <OptimizedImage
                    src="/sponsors/HST_logo.png"
                    alt="HST Pathways"
                    w="100%"
                    maxW="200px"
                    h="46px"
                    objectFit="contain"
                    objectPosition="left center"
                  />
                </Link>
              </GridItem>

              <GridItem>
                <Text color="ink.600" fontSize="md" lineHeight="1.75" maxW="3xl">
                  Our estimates run on Catalyst, the patient cost-estimate platform from HST
                  Pathways — the same technology partner behind our scheduling and clinical systems.
                  It connects directly to your insurer, so the number reflects your actual plan
                  rather than a published average.
                </Text>
              </GridItem>
            </Grid>
          </Reveal>
        </Container>
      </Box>

      {/* ── Closing CTA — deep ink band. The facility exterior sits far
             back as texture in place of the conference-room render. ──── */}
      <InkBand image="/facility/exterior.jpg">
        <Container maxW="container.xl">
          <Reveal>
            <VStack spacing={7} textAlign="center" maxW="3xl" mx="auto">
              <Heading size="2xl" color="white" fontWeight="600" letterSpacing="-0.035em" lineHeight="1.15">
                Questions about cost or coverage?
              </Heading>
              <Text fontSize="lg" color="whiteAlpha.900" maxW="xl" lineHeight="1.7">
                Our business office answers pricing questions directly — no phone tree, no runaround.
              </Text>
              <HStack spacing={4} pt={2} flexWrap="wrap" justify="center">
                <Button
                  as="a"
                  href="tel:+13213213935"
                  size="lg"
                  px={10}
                  py={7}
                  variant="brandOnDark"
                  fontSize="md"
                  leftIcon={<Icon as={FaPhone} />}
                >
                  (321) 321-3935
                </Button>
                <Button
                  as={RouterLink}
                  to="/contact"
                  size="lg"
                  px={10}
                  py={7}
                  color="white"
                  fontSize="md"
                  fontWeight="600"
                  sx={clearGlass}
                  transition="transform 0.35s cubic-bezier(0.16,1,0.3,1), background-color 0.2s"
                  _hover={{ bg: 'whiteAlpha.200', transform: 'translateY(-2px)' }}
                  _active={{ bg: 'whiteAlpha.300' }}
                  rightIcon={<FaChevronRight />}
                >
                  Contact Us
                </Button>
              </HStack>
            </VStack>
          </Reveal>
        </Container>
      </InkBand>
    </Box>
  );
};

export default PriceTransparency;
